import { Observer, ReplaySubject, Subject, distinctUntilChanged, distinctUntilKeyChanged, filter } from "rxjs";

export class CacheListener<T> extends ReplaySubject<T> {
    private currentValue: T | null;
    private promise: Promise<T> | null = null;
    private _sync: (...args: any[]) => Promise<T>;
    private _clearSubject: Subject<void> = new Subject();
    private bindings: CacheListener<any>[] = [];

    constructor(value: T, sync: (...args: any[]) => Promise<T>) {
        super(1);
        this.currentValue = value; // Initialize currentValue
        this._sync = sync;

        if (value !== null) this.next(value);
    }

    async get() {
        if (this.promise) return await this.promise;
        if (this.currentValue == null) return await this.sync();

        return this.currentValue;
    }

    onValue (callback: (arg0: T) => any) {
        this.get();

        return this.subscribe(callback);
    }

    async sync(...args: any[]) {
        if (this.promise) {
            return await this.promise;
        }

        try {
            const values = await Promise.all(this.bindings.map(binding => binding.get()));

            if (values.some(value => value === null || value === undefined)) {
                this.clear();
                return;
            }


            this.promise = this._sync(...args);
            this.currentValue = await this.promise;

            if (this.currentValue === null) {
                this.clear();
                return;
            }

            this.next(this.currentValue);

            return this.currentValue;
        } finally {
            this.promise = null; // Reset promise
        }
    }

    async clear() {
        if (this.currentValue === null) return;
        this.currentValue = null;
        this.promise = null;

        this._clearSubject.next();
    }

    onClear (observer: Partial<Observer<void>> | (() => void)) {
        return this._clearSubject.pipe(filter(() => this.currentValue == null)).subscribe(observer);
    }

    bind (listener: CacheListener<any>, { required }: { required: boolean } = { required: true }) {
        if (required) this.bindings.push(listener);
        return listener.onClear(this.clear.bind(this));
    }

    syncWhenPropertyChanges<X> (listener: CacheListener<X>, property: keyof X, ) {
        return listener.pipe(distinctUntilKeyChanged(property)).subscribe(() => this.sync());
    }

    syncWhenPropertiesChanges<X> (listener: CacheListener<X>, properties: Array<keyof X>) {
        return listener.pipe(distinctUntilChanged((prev, cur) =>
            properties.every(prop => prev[prop] === cur[prop])
        )).subscribe(() => this.sync());
    }

    syncWhenChanges (listener: CacheListener<any>) {
        return listener.pipe(distinctUntilChanged((prev, cur) =>
            prev === cur
        )).subscribe(() => this.sync());
    }

    syncWhen<X> (listener: CacheListener<X>, callback: (prev: X, cur: X) => boolean) {
        return listener.pipe(distinctUntilChanged(callback)).subscribe(() => this.sync());
    }
}
